import { useCallback, useEffect, useState } from 'react';
import { ApiError } from './api';

export type Loaded<T> =
  | { readonly state: 'loading'; readonly data: T | null }
  | { readonly state: 'ready'; readonly data: T }
  | { readonly state: 'error'; readonly data: T | null; readonly message: string; readonly status: number };

/**
 * One fetch, keyed on whatever the caller says matters. The previous answer is
 * kept while the next loads, so paging through a table does not blank it
 * between pages; `reload` re-runs the same fetch after a write.
 */
export const useLoad = <T>(
  load: () => Promise<T>,
  deps: readonly unknown[],
): Loaded<T> & { readonly reload: () => void } => {
  const [result, setResult] = useState<Loaded<T>>({ state: 'loading', data: null });
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let live = true;
    setResult((current) => ({ state: 'loading', data: current.data }));
    load()
      .then((data) => live && setResult({ state: 'ready', data }))
      .catch((caught: unknown) => {
        if (!live) return;
        setResult((current) => ({
          state: 'error',
          data: current.data,
          message: caught instanceof Error ? caught.message : 'Something went wrong.',
          status: caught instanceof ApiError ? caught.status : 0,
        }));
      });
    return () => {
      live = false;
    };
    // The caller names its own dependencies; `load` is a fresh closure each
    // render and would defeat the point.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);

  const reload = useCallback(() => setTick((n) => n + 1), []);
  return { ...result, reload };
};
